import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import PseudoField from "../forms/PseudoField";
import { useGameCtx } from "../../context/GameContext";
import { getGuestPseudo } from "../../utils/localStorage";

export default function IdentityChoiceModal({ isOpen, onClose, onContinue }) {
  const navigate = useNavigate();
  const { selectedCategory, ensureAnon, setPlayer } = useGameCtx();
  const [mode, setMode] = useState("anon");
  const [pseudo, setPseudo] = useState(() => getGuestPseudo());
  const [pseudoStatus, setPseudoStatus] = useState({ available: false });
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMode("anon");
      setPseudo(getGuestPseudo());
      setErrorMessage("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAnon = async () => {
    const trimmedPseudo = pseudo.trim();

    if (!trimmedPseudo) {
      setErrorMessage("Merci d'entrer un pseudo.");
      return;
    }

    if (trimmedPseudo.length < 3) {
      setErrorMessage("Le pseudo doit contenir au moins 3 caracteres.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    try {
      const anon = await ensureAnon(trimmedPseudo);
      setPlayer({
        id: anon.uuid,
        pseudo: anon.pseudo,
        isAnon: true,
        guest_id: anon.guest_id,
      });
      onContinue?.(anon);
    } catch (err) {
      console.error(err);
      setErrorMessage(err.message || "Impossible de valider ce pseudo.");
      if (err.suggestedPseudo) {
        setPseudo(err.suggestedPseudo);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAuthRedirect = () => {
    if (selectedCategory) {
      localStorage.setItem("pendingCategory", JSON.stringify(selectedCategory));
    }
    navigate("/authentification");
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex min-h-dvh items-center justify-center overflow-y-auto bg-black bg-opacity-50 px-4 py-8">
      <div className="relative my-auto w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-xl">
        <h2 className="mb-4 text-2xl font-semibold">
          {mode === "choice" ? "Qui etes-vous ?" : "Votre pseudo de joueur"}
        </h2>

        {mode === "choice" ? (
          <>
            <p className="mb-6 text-gray-600">
              Choisissez comment vous souhaitez jouer sur PixyQuiz.
            </p>
            <div className="flex flex-col gap-4">
              <button
                type="button"
                onClick={handleAuthRedirect}
                className="rounded-lg bg-blue-600 py-3 text-white transition hover:bg-blue-700"
              >
                Se connecter / Creer un compte
              </button>
              <button
                type="button"
                onClick={() => setMode("anon")}
                className="rounded-lg bg-gray-100 py-3 text-gray-800 transition hover:bg-gray-200"
              >
                Continuer en invite
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-2 text-sm text-gray-500 hover:underline"
              >
                Annuler
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mb-4 text-gray-600">
              Entrez un pseudo unique avant de rejoindre la partie.
            </p>
            <PseudoField
              value={pseudo}
              onChange={setPseudo}
              label=""
              placeholder="Votre pseudo"
              inputClassName="w-full rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              helperClassName="text-left text-xs"
              onAvailabilityChange={setPseudoStatus}
            />
            {errorMessage ? (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-left text-sm text-red-700">
                {errorMessage}
              </div>
            ) : null}
            <button
              type="button"
              onClick={handleAnon}
              disabled={loading || !pseudo.trim() || pseudoStatus.available === false}
              className="mt-4 w-full rounded-lg bg-green-600 py-3 text-white transition hover:bg-green-700 disabled:opacity-60"
            >
              {loading ? "Verification..." : "Continuer"}
            </button>
            <div className="mt-3 flex justify-center gap-4 text-sm">
              <button
                type="button"
                onClick={() => setMode("choice")}
                className="text-gray-500 hover:underline"
              >
                Connexion
              </button>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-500 hover:underline"
              >
                Annuler
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
